import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { badgeColorFromStatus } from '@fleet/utilities/color-utilities';
import {
  resolveStatusLabel,
  StatusLabelOverride,
} from '@fleet/utilities/status-utilities';

interface VariationConfig {
  color: string;
  size: string;
  variation: string;
  dark?: boolean;
}

interface ColorConfig {
  color: string;
  size: string;
  variation: string;
  dark: boolean;
}

interface VariationConfig {
  color: string;
  size: string;
  variation: string;
  dark?: boolean;
}

interface ColorConfig {
  color: string;
  size: string;
  variation: string;
  dark: boolean;
}

@Component({
  selector: 'fleet-badge',
  templateUrl: './badge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, MatProgressSpinnerModule],
  styles: [
    `
      fleet-badge .fleet-badge-spinner.mat-mdc-progress-spinner {
        --fuse-controls: var(--fleet-badge-spinner-color) !important;
      }
      fleet-badge
        .fleet-badge-spinner.mat-mdc-progress-spinner
        .mdc-circular-progress__determinate-circle,
      fleet-badge
        .fleet-badge-spinner.mat-mdc-progress-spinner
        .mdc-circular-progress__indeterminate-circle-graphic {
        stroke: var(--fleet-badge-spinner-color) !important;
      }

      @media print {
        fleet-badge,
        fleet-badge [class],
        fleet-badge [class] * {
          background: transparent !important;
          background-color: transparent !important;
          color: #000 !important;
          border: 0 !important;
          box-shadow: none !important;
          outline: none !important;
          padding: 0 !important;
          margin: 0 !important;
          border-radius: 0 !important;
          min-width: 0 !important;
          min-height: 0 !important;
          font-weight: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
        }

        fleet-badge [class] svg,
        fleet-badge [class] button,
        fleet-badge [class] mat-spinner,
        fleet-badge [class] .mat-mdc-progress-spinner {
          display: none !important;
        }

        fleet-badge + fleet-badge,
        fleet-platenumber-badge + fleet-medallion-badge,
        fleet-medallion-badge + fleet-platenumber-badge,
        fleet-platenumber-badge + fleet-platenumber-badge,
        fleet-medallion-badge + fleet-medallion-badge {
          margin-left: 0.5ch !important;
        }
      }
    `,
  ],
})
export class BadgeComponent {
  // Signal inputs
  // statusLabelOverrides: display text only — colours still use `status`. Bind a shared
  // constant ref from status-utilities.ts in lists, not a per-row getter.
  label = input<string>('');
  status = input<string>('');
  statusLabelOverrides = input<StatusLabelOverride[]>([]);
  size = input<string>('small');
  variationConfig = input<VariationConfig | null>(null);
  showSpinner = input<boolean>(false);

  // Signal output
  removeBadge = output<void>();

  // Computed signals
  colorConfig = computed<ColorConfig>(() => {
    const status = this.status();
    const config = this.variationConfig();

    if (status) {
      return {
        color: badgeColorFromStatus(status).badgeColor,
        size: this.size(),
        variation: 'flat-pill',
        dark: true,
      };
    }

    return config
      ? { ...config, dark: config.dark ?? false }
      : {
          color: 'green',
          size: this.size(),
          variation: 'flat-pill',
          dark: false,
        };
  });

  processedLabel = computed(() =>
    resolveStatusLabel(
      this.status(),
      this.statusLabelOverrides(),
      this.label(),
    ),
  );

  variationClass = computed(() => {
    const config = this.colorConfig();
    return this.buildVariationClass(config);
  });

  textSizeClass = computed(() => {
    const s = this.colorConfig().size;
    return s === 'large' || s === 'medium' ? 'text-sm' : 'text-xs';
  });

  svgClass = computed(() => {
    const config = this.colorConfig();
    const variation = config.variation;

    if (variation === 'flat-dot' || variation === 'border-and-dot') {
      return this.fillColour(config.color);
    }
    if (
      variation === 'flat-pill-with-remove' ||
      variation === 'border-and-remove' ||
      variation === 'border'
    ) {
      return this.strokeColour(config.color);
    }

    if (variation === 'outline-pill') {
      return this.outlineBorderColor(config.color);
    }

    return '';
  });

  buttonClass = computed(() => {
    const config = this.colorConfig();
    const variation = config.variation;

    if (
      variation === 'flat-pill-with-remove' ||
      variation === 'border-and-remove'
    ) {
      return this.buttonClasses(config.color);
    }
    return '';
  });

  withDot = computed(() => {
    const variation = this.colorConfig().variation;
    return variation === 'flat-dot' || variation === 'border-and-dot';
  });

  withButton = computed(() => {
    const variation = this.colorConfig().variation;
    return (
      variation === 'flat-pill-with-remove' || variation === 'border-and-remove'
    );
  });

  spinnerColor = computed(() => {
    const config = this.colorConfig();
    return this.getSpinnerColorValue(config.color, config.dark);
  });

  spinnerStyle = computed(() => {
    const color = this.spinnerColor();
    return `--fleet-badge-spinner-color: ${color}`;
  });

  styles = computed<Record<string, string> | null>(() => {
    const config = this.colorConfig();
    if (
      config.variation === 'badge-with-hex' &&
      /^#[0-9A-F]{6}$/i.test(config.color)
    ) {
      return { 'background-color': config.color };
    }
    return null;
  });

  returnSize(size: string): string {
    switch (size) {
      case 'small':
        return 'px-2 py-1';
      case 'medium':
        return 'px-2.5 py-1.5';
      case 'large':
        return 'px-3 py-2';
      default:
        return 'px-2 py-1';
    }
  }

  /** Vertical padding reduced by 1px per side vs flat-pill so total height matches with 1px border. */
  returnOutlinePillSize(size: string): string {
    switch (size) {
      case 'small':
        return 'px-2 py-[3px]';
      case 'medium':
        return 'px-2.5 py-[5px]';
      case 'large':
        return 'px-3 py-[7px]';
      default:
        return 'px-2 py-[3px]';
    }
  }

  textColourAndBackground(color: string, dark: boolean) {
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      return dark
        ? `bg-[${color}] text-white`
        : `bg-[${color}-100] text-[${color}]`;
    }

    const defaultDark = 'bg-' + color + ' text-white';
    const colourWithoutNumber = color.replace(/[-\d]/g, '');
    const defaultLight =
      'bg-' +
      colourWithoutNumber +
      '-100' +
      ' ' +
      'text-' +
      colourWithoutNumber +
      '-700';
    switch (color) {
      case 'green':
        return dark ? 'bg-green-700 text-white' : 'bg-green-100 text-green-700';
      case 'red':
        return dark ? 'bg-red-700 text-white' : 'bg-red-100 text-red-700';
      case 'yellow':
        return dark
          ? 'bg-yellow-700 text-white'
          : 'bg-yellow-100 text-yellow-700';
      case 'gray':
        return dark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700';
      case 'gray-500':
        return dark ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-500';
      case 'blue':
        return dark ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-700';
      case 'blue-500':
        return dark ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-500';

      case 'indigo':
        return dark
          ? 'bg-indigo-700 text-white'
          : 'bg-indigo-100 text-indigo-700';
      case 'purple':
        return dark
          ? 'bg-purple-700 text-white'
          : 'bg-purple-100 text-purple-700';
      case 'pink':
        return dark ? 'bg-pink-700 text-white' : 'bg-pink-100 text-pink-700';
      case 'orange':
        return dark
          ? 'bg-orange-700 text-white'
          : 'bg-orange-100 text-orange-700';
      case 'orange-500':
        return dark
          ? 'bg-orange-500 text-white'
          : 'bg-orange-50 text-orange-500';
      case 'amber':
        return dark ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-700';
      case 'amber-500':
        return dark ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-500';
      case 'emerald':
        return dark
          ? 'bg-emerald-700 text-white'
          : 'bg-emerald-100 text-emerald-700';
      case 'emerald-500':
        return dark
          ? 'bg-emerald-500 text-white'
          : 'bg-emerald-50 text-emerald-500';
      case 'lime-500':
        return dark ? 'bg-lime-500 text-white' : 'bg-lime-50 text-lime-500';

      case 'green-500':
        return dark ? 'bg-green-500 text-white' : 'bg-green-50 text-green-500';
      case 'red-600':
        return dark ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600';
      default:
        return dark ? defaultDark : defaultLight;
    }
  }

  textColour(color: string) {
    switch (color) {
      case 'green':
        return 'text-green-700';
      case 'red':
        return 'text-red-700';
      case 'yellow':
        return 'text-yellow-700';
      case 'gray':
        return 'text-gray-700';
      case 'blue':
        return 'text-blue-700';
      case 'indigo':
        return 'text-indigo-700';
      case 'purple':
        return 'text-purple-700';
      case 'pink':
        return 'text-pink-700';
      default:
        return 'text-green-700';
    }
  }

  outlineBorderColor(color: string): string {
    switch (color) {
      case 'green':
        return 'border border-green-500 dark:border-green-400';
      case 'red':
        return 'border border-red-500 dark:border-red-400';
      case 'yellow':
        return 'border border-yellow-500 dark:border-yellow-400';
      case 'gray':
        return 'border border-gray-500 dark:border-gray-400';
      case 'blue':
        return 'border border-blue-500 dark:border-blue-400';
      case 'indigo':
        return 'border border-indigo-500 dark:border-indigo-400';
      case 'purple':
        return 'border border-purple-500 dark:border-purple-400';
      case 'pink':
        return 'border border-pink-500 dark:border-pink-400';
      default:
        return 'border border-green-500 dark:border-green-400';
    }
  }

  outlineTextColor(color: string): string {
    switch (color) {
      case 'green':
        return 'text-green-700 dark:text-green-300';
      case 'red':
        return 'text-red-700 dark:text-red-300';
      case 'yellow':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'gray':
        return 'text-gray-700 dark:text-gray-300';
      case 'blue':
        return 'text-blue-700 dark:text-blue-300';
      case 'indigo':
        return 'text-indigo-700 dark:text-indigo-300';
      case 'purple':
        return 'text-purple-700 dark:text-purple-300';
      case 'pink':
        return 'text-pink-700 dark:text-pink-300';
      default:
        return 'text-green-700 dark:text-green-300';
    }
  }

  strokeColour(color: string) {
    switch (color) {
      case 'green':
        return 'stroke-green-600/50 group-hover:stroke-green-600/75';
      case 'yellow':
        return 'stroke-yellow-600/50 group-hover:stroke-yellow-600/75';
      case 'gray':
        return 'stroke-gray-600/50 group-hover:stroke-gray-600/75';
      case 'blue':
        return 'stroke-blue-600/50 group-hover:stroke-blue-600/75';
      case 'indigo':
        return 'stroke-indigo-600/50 group-hover:stroke-indigo-600/75';
      case 'purple':
        return 'stroke-purple-600/50 group-hover:stroke-purple-600/75';
      case 'pink':
        return 'stroke-pink-600/50 group-hover:stroke-pink-600/75';

      case 'red':
        return 'stroke-red-600/50 group-hover:stroke-red-600/75';
      case 'emerald-500':
      case 'green-500':
      case 'red-600':
      case 'lime-500':
      case 'blue-500':
      case 'orange-500':
        return 'stroke-white ';
      default:
        return 'stroke-white';
    }
  }

  buttonClasses(color: string) {
    switch (color) {
      case 'green':
        return 'hover:bg-green-800/20';
      case 'yellow':
        return 'hover:bg-yellow-800/20';
      case 'gray':
        return 'hover:bg-gray-800/20';
      case 'blue':
        return 'hover:bg-blue-800/20';
      case 'indigo':
        return 'hover:bg-indigo-800/20';
      case 'purple':
        return 'hover:bg-purple-800/20';
      case 'pink':
        return 'hover:bg-pink-800/20';
      case 'red':
        return 'hover:bg-red-800/20';
      case 'emerald-500':
        return 'hover:bg-emerald-800/20 text-white';
      default:
        return 'hover:bg-green-800/20';
    }
  }

  ringColour(color: string) {
    switch (color) {
      case 'green':
        return 'ring-green-500/10';

      case 'yellow':
        return 'ring-yellow-500/10';
      case 'gray':
        return 'ring-gray-500/10';
      case 'blue':
        return 'ring-blue-500/10';
      case 'indigo':
        return 'ring-indigo-500/10';
      case 'purple':
        return 'ring-purple-500/10';
      case 'pink':
        return 'ring-pink-500/10';

      case 'red':
        return 'ring-red-500/10';

      default:
        return 'ring-green-500/10';
    }
  }

  fillColour(color: string) {
    switch (color) {
      case 'green':
        return 'fill-green-500';
      case 'yellow':
        return 'fill-yellow-500';

      case 'gray':
        return 'fill-gray-500';

      case 'blue':
        return 'fill-blue-500';

      case 'indigo':
        return 'fill-indigo-500';

      case 'purple':
        return 'fill-purple-500';

      case 'pink':
        return 'fill-pink-500';

      case 'red':
        return 'fill-red-500';

      default:
        return 'fill-green-500';
    }
  }

  private getSpinnerColorValue(color: string, dark: boolean): string {
    if (dark) {
      return '#ffffff';
    }

    if (/^#[0-9A-F]{6}$/i.test(color)) {
      return color;
    }

    // Tailwind color mappings
    switch (color) {
      case 'green':
        return '#15803d'; // green-700
      case 'red':
        return '#b91c1c'; // red-700
      case 'yellow':
        return '#a16207'; // yellow-700
      case 'gray':
        return '#374151'; // gray-700
      case 'gray-500':
        return '#6b7280'; // gray-500
      case 'blue':
        return '#1d4ed8'; // blue-700
      case 'blue-500':
        return '#3b82f6'; // blue-500
      case 'indigo':
        return '#4338ca'; // indigo-700
      case 'purple':
        return '#7e22ce'; // purple-700
      case 'pink':
        return '#be185d'; // pink-700
      case 'orange':
        return '#c2410c'; // orange-700
      case 'orange-500':
        return '#f97316'; // orange-500
      case 'amber':
        return '#b45309'; // amber-700
      case 'amber-500':
        return '#f59e0b'; // amber-500
      case 'emerald':
        return '#047857'; // emerald-700
      case 'emerald-500':
        return '#10b981'; // emerald-500
      case 'lime-500':
        return '#84cc16'; // lime-500
      case 'green-500':
        return '#22c55e'; // green-500
      case 'red-600':
        return '#dc2626'; // red-600
      default:
        return '#15803d'; // default green-700
    }
  }

  private buildVariationClass(config: ColorConfig): string {
    const { variation, color, size, dark } = config;

    switch (variation) {
      case 'badge-with-hex':
        return (
          'grid min-h-[18px] min-w-[18px] place-items-center rounded-full p-1 ' +
          this.textColourAndBackground(color, dark)
        );

      case 'flat-pill':
        return (
          'rounded-full ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.returnSize(size)
        );

      case 'flat-pill-with-remove':
        return (
          'rounded-full ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.returnSize(size)
        );

      case 'with-border':
        return (
          'rounded-md ' +
          this.ringColour(color) +
          ' ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.ringColour(color) +
          ' ' +
          this.returnSize(size)
        );

      case 'flat-dot':
        return (
          'rounded-md gap-x-1.5 ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.returnSize(size)
        );

      case 'border-and-dot':
        return (
          'rounded-md gap-x-1.5 ring-gray-200 ' +
          this.textColour(color) +
          ' ' +
          this.returnSize(size)
        );

      case 'border-and-remove':
        return (
          'gap-x-0.5 rounded-md ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.ringColour(color) +
          ' ' +
          this.returnSize(size)
        );

      case 'border':
        return (
          'gap-x-0.5 rounded-md ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.ringColour(color) +
          ' ' +
          this.returnSize(size)
        );

      case 'outline-pill':
        return (
          'rounded-full border bg-transparent ' +
          this.outlineBorderColor(color) +
          ' ' +
          this.outlineTextColor(color) +
          ' ' +
          this.returnOutlinePillSize(size)
        );

      default:
        return (
          'rounded-full ' +
          this.textColourAndBackground(color, dark) +
          ' ' +
          this.returnSize(size)
        );
    }
  }
}
